import {
  DEFAULT_LOCALE,
  DEFAULT_THEME,
  DEFAULT_TIMEZONE_FALLBACK,
  STORAGE_KEYS,
  THEME_KEYS,
  type Locale,
  type ThemeKey,
} from "@/constants/config";

/** Local user preferences: theme, sidebar state, language and timezone. */

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage disabled (private mode) — preferences simply do not persist */
  }
};

const isTheme = (value: string | null): value is ThemeKey =>
  value === THEME_KEYS.light || value === THEME_KEYS.dark || value === THEME_KEYS.system;

export const readTheme = (): ThemeKey => {
  const stored = read(STORAGE_KEYS.theme);
  return isTheme(stored) ? stored : DEFAULT_THEME;
};

export const writeTheme = (theme: ThemeKey): void => write(STORAGE_KEYS.theme, theme);

export const readLocale = (): Locale | null => {
  const stored = read(STORAGE_KEYS.locale);
  return stored === "en" || stored === "id" ? stored : null;
};

export const writeLocale = (locale: Locale): void => write(STORAGE_KEYS.locale, locale);

export const readSidebarCollapsed = (): boolean => read(STORAGE_KEYS.sidebarCollapsed) === "true";

export const writeSidebarCollapsed = (collapsed: boolean): void =>
  write(STORAGE_KEYS.sidebarCollapsed, String(collapsed));

export const detectTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE_FALLBACK;
  } catch {
    return DEFAULT_TIMEZONE_FALLBACK;
  }
};

export const readTimezone = (): string => read(STORAGE_KEYS.timezone) ?? detectTimezone();

export const writeTimezone = (timezone: string): void => write(STORAGE_KEYS.timezone, timezone);

export const readInvoicePageSize = (): string | null => read(STORAGE_KEYS.invoicePageSize);

export const writeInvoicePageSize = (pageSize: string): void =>
  write(STORAGE_KEYS.invoicePageSize, pageSize);

export const browserLanguages = (): readonly string[] =>
  typeof navigator === "undefined" ? [DEFAULT_LOCALE] : [...navigator.languages, navigator.language];

/** Applies the theme class + `color-scheme` to `<html>`. */
export const applyTheme = (theme: ThemeKey): void => {
  const prefersDark =
    typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
  const resolved = theme === THEME_KEYS.system ? (prefersDark ? THEME_KEYS.dark : THEME_KEYS.light) : theme;
  const root = document.documentElement;
  root.classList.toggle(THEME_KEYS.dark, resolved === THEME_KEYS.dark);
  root.style.colorScheme = resolved;
};

export const watchSystemTheme = (listener: () => void): (() => void) => {
  const query = window.matchMedia("(prefers-color-scheme: dark)");
  query.addEventListener("change", listener);
  return () => query.removeEventListener("change", listener);
};
